// hl-custom: custom file, not in stock Horizon -- ToS acceptance custom element writing consent to cart attributes (feature: tos-acceptance)
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

const TOS_VERSION = 'v1';

/**
 * Custom element wrapping the Terms of Service acceptance checkbox.
 * On change, writes tos_accepted / tos_accepted_at / tos_version to
 * cart.attributes via the Cart AJAX API. Toggles body[data-tos-accepted]
 * and the disabled state of any [data-tos-guarded] elements so the
 * Checkout button reflects the new state without waiting for a re-render.
 *
 * @typedef {{ checkbox: HTMLInputElement }} Refs
 * @extends {Component<Refs>}
 */
class TosAcceptance extends Component {
  requiredRefs = ['checkbox'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * @param {Event} event
   */
  handleToggle = async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const accepted = event.target.checked;

    this.#applyVisualState(accepted);

    const attributes = accepted
      ? {
          tos_accepted: 'true',
          tos_accepted_at: new Date().toISOString(),
          tos_version: TOS_VERSION,
        }
      : {
          tos_accepted: '',
          tos_accepted_at: '',
          tos_version: '',
        };

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      document.dispatchEvent(
        new CustomEvent('tos:changed', { detail: { accepted } })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[tos-acceptance] failed to persist acceptance', error);
      this.#revertVisualState(!accepted);
      this.refs.checkbox.checked = !accepted;
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * @param {boolean} accepted
   */
  #applyVisualState(accepted) {
    document.body.dataset.tosAccepted = accepted ? 'true' : '';
    for (const button of document.querySelectorAll('[data-tos-guarded]')) {
      if (accepted) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /**
   * @param {boolean} accepted
   */
  #revertVisualState(accepted) {
    this.#applyVisualState(accepted);
  }
}

function shakeAcceptance() {
  const tos = document.querySelector('tos-acceptance');
  if (!(tos instanceof HTMLElement)) return;
  tos.classList.remove('tos-acceptance--shake');
  void tos.offsetWidth;
  tos.classList.add('tos-acceptance--shake');
  tos.addEventListener(
    'animationend',
    () => tos.classList.remove('tos-acceptance--shake'),
    { once: true }
  );
}

let interceptorsRegistered = false;
function registerGuardInterceptors() {
  if (interceptorsRegistered) return;
  interceptorsRegistered = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('[data-tos-guarded][aria-disabled="true"]');
      if (!button || button.hasAttribute('disabled')) return;
      event.preventDefault();
      event.stopPropagation();
      shakeAcceptance();
    },
    { capture: true }
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const guarded = form.querySelector('[data-tos-guarded][aria-disabled="true"]:not([disabled])');
      if (!guarded) return;
      event.preventDefault();
      shakeAcceptance();
    },
    { capture: true }
  );
}

registerGuardInterceptors();

if (!customElements.get('tos-acceptance')) {
  customElements.define('tos-acceptance', TosAcceptance);
}
